import { createElement, Fragment, forwardRef } from 'react';
import { useDesignTokens, renderInlineCssVars } from '../../theme';
import { Icon } from '../Icon';
import { useIconA11yInfo } from '../Icon/useIconA11yInfo';
import { ButtonVariant } from '../Button';
import { BaseButton } from '../Button/__internal__';
import { BadgeButtonProps } from '.';

import cx from 'classnames';
import buttonCn from '../Button/__internal__/BaseButton.css.json';
import cn from './BadgeButton.css.json';
import './BadgeButton.css';

export const BadgeButton = forwardRef<HTMLButtonElement, BadgeButtonProps>(function BadgeButton(
  {
    badgeDesign,
    children,
    icon,
    value,
    variant,
    tooltip: customTooltip,
    ariaLabel: customAriaLabel,
    ...props
  },
  ref
) {
  const badgeValue = value && value > 999 ? '999+' : value;
  const valueValid = value && value > 0;

  const dt = useDesignTokens();
  let squareBadgeStyle;
  if (badgeDesign === 'square') {
    squareBadgeStyle = renderInlineCssVars(
      {
        bg: dt.c.colorSecondary,
        fg: dt.themeVariant === 'dark' ? dt.c.colorTextDefault : dt.c.colorTextInverse,
      },
      'badge-button'
    );
  }
  const token = renderInlineCssVars(
    {
      'spacing-xs': dt.spacing.core.xs,
      'font-size-s': dt.fontNova.size.s,
      'font-weight-thick': dt.fontNova.weight.thick,
    },
    'badge-button-text'
  );

  const badgeSpan = (
    <span className={cn.text} style={token} dir="ltr">
      {badgeValue}
    </span>
  );

  const badgeIcon = icon ? (
    <Icon {...icon} className={cx(buttonCn.iconInner, icon.className)} tooltip="" ariaLabel="" />
  ) : undefined;

  let badgeContent;
  if (badgeDesign === 'square') {
    if (valueValid) {
      badgeContent = badgeSpan;
    } else {
      badgeContent = badgeIcon;
    }
  } else {
    badgeContent = (
      <Fragment>
        {badgeIcon}
        {badgeSpan}
      </Fragment>
    );
  }

  const getIconA11yInfo = useIconA11yInfo();
  const { tooltip: iconTooltip = undefined } = icon ? getIconA11yInfo(icon) : {};
  const tooltip =
    customTooltip ?? (badgeDesign !== 'square' || !valueValid ? iconTooltip : undefined);
  const ariaLabel = customAriaLabel ?? tooltip;

  return (
    <BaseButton
      variant={variant ? variant : ButtonVariant.Icon}
      {...props}
      tooltip={tooltip}
      ariaLabel={ariaLabel}
      ref={ref}
      style={squareBadgeStyle}
    >
      <div
        className={cx(
          cn.basic,
          valueValid && badgeDesign === 'square' && cn.square,
          badgeSpan && icon && badgeDesign !== 'square' && cn.withTextIcon
        )}
      >
        {badgeContent}
      </div>
    </BaseButton>
  );
});
